import React from 'react';
import type {SearchCounts} from '../utils/Analytics/schema';
import type {PlaceResults, StationResults} from '../utils/MyWeather/search';

type SearchState = {
  query: string | null;
  places: PlaceResults;
  stations: StationResults;
};

/**
 * GA `search_results` for My weather's search (#379, source `my_weather`),
 * in both apps: `track` gets the counts once when both sources first came
 * back empty, and once at the pick (the returned function), each at most
 * once per search session, from the first query to a clear. Counts only,
 * never the query; a source that failed counts nothing; nothing typed (the
 * ⌖'s list) reports nothing.
 *
 * The counts are read at the pick through a ref set in the commit, so the
 * returned function stays the same while the user types, and a pick right
 * after an answer lands never reads the render before it.
 */
const useMyWeatherSearchSession = (
  {query, places, stations}: SearchState,
  track: (counts: SearchCounts) => void,
) => {
  const counts = React.useMemo(
    () => ({
      placeResults: places.status === 'ready' ? places.items.length : undefined,
      stationResults: stations.status === 'ready' ? stations.count : undefined,
    }),
    [places.status, places.items.length, stations.status, stations.count],
  );
  const trackRef = React.useRef(track);
  const reportedEmpty = React.useRef(false);
  const reportedPick = React.useRef(false);
  const session = React.useRef({query, counts});
  React.useLayoutEffect(() => {
    trackRef.current = track;
    session.current = {query, counts};
  });
  React.useEffect(() => {
    if (query === null) {
      reportedEmpty.current = false;
      reportedPick.current = false;
    }
  }, [query]);

  const empty =
    query !== null &&
    places.status === 'ready' &&
    stations.status === 'ready' &&
    places.items.length === 0 &&
    stations.count === 0;
  React.useEffect(() => {
    if (empty && !reportedEmpty.current) {
      reportedEmpty.current = true;
      trackRef.current(counts);
    }
  }, [empty, counts]);

  return React.useCallback(() => {
    if (session.current.query !== null && !reportedPick.current) {
      reportedPick.current = true;
      trackRef.current(session.current.counts);
    }
  }, []);
};

export default useMyWeatherSearchSession;
